import React, { useEffect, useRef, useState } from 'react';
import { App as AntdApp, Typography, Button, Divider, Space, InputNumber, theme, Upload } from 'antd';
import { DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import PairwiseInput from '../components/PairwiseInput';
import PairwiseMatrix from '../components/PairwiseMatrix';
import LFuzzyMatrix, { convertMatrixToValues } from '../components/AhpFuzzyMatrix';
import FuzzyMatrix from '../components/AhpFuzzyMatrixTfn';
import Results from '../components/Results';
import { AhpWeights } from '../api/fetchWeights';
import {
  importAHPMatrixFlexibleFile,
} from '../utils/matrixImport';
import { validateAHP, validateFuzzyAHP, validateLinguisticFuzzyAHP } from '../utils/validators';
import { exportMatrixXlsx } from '../utils/matrixExport';

const { Title, Text } = Typography;

function Ahp({ variant, methodSelector, methodChanged, criteriaCount, criteria, updateCriteria }) {
  const isImportingRef = useRef(false);
  const [stage, setStage] = useState('number'); // number | text | null | edit
  const [localCount, setLocalCount] = useState(criteria.length || criteriaCount || 0);
  const [tempCriteria, setTempCriteria] = useState([]);
  const [matrix, setMatrix] = useState([]);

  const [crisp_weights, setWeights] = useState([]);
  const [lambdaMax, setLambdaMax] = useState(null);
  const [ci, setCi] = useState(null);
  const [cr, setCr] = useState(null);
  const [inconsistency_ratios, setInconsistencyRatio] = useState([]);

  const [sorted_criteria, setSortedCriteria] = useState([]);
  const [lower_weights, setLWeights] = useState([]);
  const [upper_weights, setUWeights] = useState([]);
  const [extra, setExtra] = useState({});

  const [fileName] = useState('');
  const { token } = theme.useToken();
  const { message: messageApi, modal } = AntdApp.useApp();

  const templateHrefMap = {
    origin: `${process.env.PUBLIC_URL}/templates/AHP_matrix_template.xlsx`,
    fuzzy: `${process.env.PUBLIC_URL}/templates/AHP_fuzzy_tfn_template.xlsx`,
    'linguistic fuzzy': `${process.env.PUBLIC_URL}/templates/AHP_linguistic_template.xlsx`,
  };

  const templateHref = templateHrefMap[variant];

  const resetResults = () => {
    setWeights([]);
    setLWeights([]);
    setUWeights([]);
    setSortedCriteria([]);
    setLambdaMax(null);
    setCi(null);
    setCr(null);
    setInconsistencyRatio([]);
    setExtra({});
  };

  const handleImport = async (file) => {
    try {
      const {
        criteria: parsedCriteria,
        matrix: parsedMatrix,
      } = await importAHPMatrixFlexibleFile(file, variant);

      let validation;
      if (variant === 'origin') {
        validation = validateAHP(parsedCriteria, parsedMatrix);
      } else if (variant === 'fuzzy') {
        validation = validateFuzzyAHP(parsedCriteria, parsedMatrix);
      } else if (variant === 'linguistic fuzzy') {
        validation = validateLinguisticFuzzyAHP(parsedCriteria, parsedMatrix);
      } else {
        validation = { ok: true, errors: [] };
      }

      if (!validation?.ok) {
        modal.error({
          title: 'Invalid AHP matrix',
          content: <div>{validation?.errors?.map((e, i) => <div key={i}>- {e}</div>)}</div>,
          width: 560,
        });
        return false;
      }

      isImportingRef.current = true;
      updateCriteria(parsedCriteria);
      setMatrix(parsedMatrix);
      setLocalCount(parsedCriteria.length);
      setStage(null);
      resetResults();
      messageApi.success('AHP matrix imported successfully.');
    } catch (e) {
      modal.error({ title: 'Import error', content: e.message || String(e) });
    }

    return false;
  };

  const handleSetCriteriaNumber = () => {
    const count = Math.max(2, Math.min(100, localCount));
    setLocalCount(count);
    updateCriteria(Array.from({ length: count }, (_, i) => `Criterion ${i + 1}`));
    setStage('text');
  };

  const handleConfirmCriteria = () => {
    const count = criteria.length;
    const diagonalValue =
      variant === 'linguistic fuzzy' ? 'EI' : variant === 'fuzzy' ? [1, 1, 1] : 1;
    const initialValue =
      variant === 'linguistic fuzzy' ? 'EI' : variant === 'fuzzy' ? [1, 1, 1] : 1;

    setMatrix(
      Array.from({ length: count }, (_, i) =>
        Array.from({ length: count }, (_, j) => (i === j ? diagonalValue : initialValue))
      )
    );
    setStage(null);
  };

  useEffect(() => {
    if (!criteria.length) return;
    if (isImportingRef.current) {
      isImportingRef.current = false;
      return;
    }

    const count = criteria.length;
    const diagonalValue =
      variant === 'linguistic fuzzy' ? 'EI' : variant === 'fuzzy' ? [1, 1, 1] : 1;
    const initialValue =
      variant === 'linguistic fuzzy' ? 'EI' : variant === 'fuzzy' ? [1, 1, 1] : 1;

    setMatrix(
      Array.from({ length: count }, (_, i) =>
        Array.from({ length: count }, (_, j) => (i === j ? diagonalValue : initialValue))
      )
    );
    resetResults();
    setStage(null);
  }, [criteria.length, methodChanged, variant]);

  const handleSubmit = async () => {
    const activeCriteria = criteria.filter((c) => c.trim() !== '');
    if (activeCriteria.length < 2) {
      alert('Please define at least two valid criteria.');
      return;
    }

    const numericMatrix = convertMatrixToValues(matrix);
    const processedMatrix = variant === 'linguistic fuzzy' ? numericMatrix : matrix;

    const payload = {
      variant,
      n: criteria.length,
      criteria,
      bestIdx: null,
      worstIdx: null,
      matrix: processedMatrix,
    };

    try {
      const {
        crisp_weights: resultWeights,
        lower_weights: resultLower,
        upper_weights: resultUpper,
        sorted_criteria: resultSorted,
        lambdaMax: resultLambdaMax,
        ci: resultCi,
        cr: resultCr,
        inconsistency_ratios: resultRatios,
        extra: resultExtra,
      } = await AhpWeights(payload);

      setWeights(resultWeights || []);
      setLWeights(resultLower || []);
      setUWeights(resultUpper || []);
      setSortedCriteria(resultSorted || []);
      setLambdaMax(resultLambdaMax ?? null);
      setCi(resultCi);
      setCr(resultCr);
      setInconsistencyRatio(resultRatios || []);
      setExtra(resultExtra || {});
    } catch (err) {
      alert(err.message);
    }
  };

  const handleReset = () => {
    setStage('number');
    updateCriteria([]);
    setLocalCount(0);
    setMatrix([]);
    resetResults();
  };

  const handleEdit = () => {
    setTempCriteria([...criteria]);
    setStage('edit');
  };

  const handleSaveEdit = () => {
    const count = tempCriteria.length;
    const diagonalValue =
      variant === 'linguistic fuzzy' ? 'EI' : variant === 'fuzzy' ? [1, 1, 1] : 1;
    const defaultValue =
      variant === 'linguistic fuzzy' ? 'EI' : variant === 'fuzzy' ? [1, 1, 1] : 1;

    const newMatrix = Array.from({ length: count }, (_, i) =>
      Array.from({ length: count }, (_, j) => {
        if (i === j) return diagonalValue;
        return matrix?.[i]?.[j] ?? defaultValue;
      })
    );

    updateCriteria([...tempCriteria]);
    setMatrix(newMatrix);
    resetResults();
    setStage(null);
  };

  const handleExportXlsx = () => {
    exportMatrixXlsx({
      method: 'ahp',
      variant,
      criteria,
      matrix,
      bestIdx: null,
      worstIdx: null,
      filename: fileName || undefined,
    });
  };

  const headerExtra = (
    <Button icon={<DownloadOutlined />} onClick={handleExportXlsx}>
      Export input matrix (.xlsx)
    </Button>
  );

  return (
    <div
      style={{
        display: 'flex',
        minHeight: '100vh',
        background: token.colorBgLayout,
      }}
    >
      <div
        style={{
          width: 420,
          background: token.colorBgContainer,
          padding: token.paddingLG,
          borderRight: `1px solid ${token.colorSplit}`,
        }}
      >
        <Title level={2} style={{ marginTop: 0, marginBottom: 0 }}>
          AHP Pairwise Analysis
        </Title>
        <Text type="secondary" style={{ display: 'block', marginTop: 6 }}>
          Configure criteria and calculate AHP weights with consistency diagnostics.
        </Text>

        <div style={{ marginTop: 24, marginBottom: 24 }}>
          {methodSelector}

          {variant === 'origin' && (
          <Typography.Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
            The AHP {variant} calculation is grounded in the foundational research detailed in the 
            publication available at:{' '} 
            <a href="https://doi.org/10.1016/0270-0255(87)90473-8" target="_blank" rel="noreferrer">
              https://doi.org/10.1016/0270-0255(87)90473-8
            </a>
          </Typography.Text>
          )}

          {(variant === 'fuzzy' || variant === 'linguistic fuzzy') && (
          <Typography.Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
            The AHP {variant} calculation is grounded in the foundational research detailed in the 
            publication available at:{' '} 
            <a href="https://doi.org/10.1016/j.fss.2009.10.011" target="_blank" rel="noreferrer">
              https://doi.org/10.1016/j.fss.2009.10.011
            </a>
          </Typography.Text>
          )}
        </div>

        <div style={{ margin: '8px 0 10px' }}>
          <Upload
            beforeUpload={handleImport}
            showUploadList={false}
            multiple={false}
            maxCount={1}
            accept={[
              'text/csv',
              'application/vnd.ms-excel',
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
              '.csv',
              '.CSV',
              '.xlsx',
              '.XLSX',
            ].join(',')}
          >
            <Button icon={<UploadOutlined />} style={{ marginTop: 16, marginBottom: 0 }}>
              Import AHP matrix (CSV/XLSX)
            </Button>
          </Upload>

          <Typography.Link
            href={templateHref}
            download
            style={{ display: 'inline-block', marginLeft: 10, textDecoration: 'underline' }}
          >
            Download AHP template
          </Typography.Link>
        </div>

        <div style={{ marginBottom: 24 }}>
          <Text strong>Number of criteria: </Text>
          {stage === 'number' ? (
            <Space style={{ marginTop: 8 }}>
              <InputNumber
                min={2}
                max={100}
                value={localCount}
                onChange={(value) => setLocalCount(value ?? 0)}
              />
              <Button type="primary" onClick={handleSetCriteriaNumber}>
                Initialize criteria
              </Button>
            </Space>
          ) : (
            <Space style={{ marginLeft: 8 }}>
              <Text>{criteria.length}</Text>
            </Space>
          )}
        </div>

        {stage === 'text' && (
          <>
            <PairwiseInput
              criteria={criteria}
              setCriteria={updateCriteria}
              editable
              allowAddRemove={false}
            />
            <Button type="primary" block style={{ marginTop: 16 }} onClick={handleConfirmCriteria}>
              Generate matrix
            </Button>
          </>
        )}

        {(stage === null || stage === 'edit') && (
          <>
            <PairwiseInput
              criteria={stage === 'edit' ? tempCriteria : criteria}
              setCriteria={stage === 'edit' ? setTempCriteria : updateCriteria}
              editable={stage === 'edit' || stage === 'text'}
              allowAddRemove={stage === 'edit'}
            />
            <div
              style={{
                marginTop: 16,
                display: 'grid',
                gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                gap: 8,
                width: '100%',
              }}
            >
              <Button type="primary" onClick={handleSubmit} style={{ width: '100%' }}>
                Calculate
              </Button>
              {stage === null ? (
                <Button onClick={handleEdit} style={{ width: '100%' }}>
                  Edit criteria
                </Button>
              ) : (
                <Button onClick={handleSaveEdit} style={{ width: '100%' }}>
                  Save changes
                </Button>
              )}

              <Button onClick={handleReset} style={{ width: '100%' }}>
                Reset
              </Button>
            </div>
          </>
        )}
      </div>

      <div style={{ flex: 1, minWidth: 0, padding: token.paddingLG, background: token.colorBgLayout }}>
        {criteria.length > 0 && matrix.length > 0 && (
          <>
            <div style={{ width: '100%', overflowX: 'auto' }}>
              {variant === 'linguistic fuzzy' ? (
                <LFuzzyMatrix matrix={matrix} setMatrix={setMatrix} criteria={criteria} extra={headerExtra} />
              ) : variant === 'fuzzy' ? (
                <FuzzyMatrix matrix={matrix} setMatrix={setMatrix} criteria={criteria} extra={headerExtra} />
              ) : (
                <PairwiseMatrix matrix={matrix} setMatrix={setMatrix} criteria={criteria} extra={headerExtra} />
              )}
            </div>

            {crisp_weights.length > 0 && lambdaMax !== null && (
              <>
                <Divider />
                <Results
                  method="ahp"
                  variant={variant?.toLowerCase?.()}
                  crisp_weights={crisp_weights}
                  lower_weights={lower_weights}
                  upper_weights={upper_weights}
                  criteria={criteria}
                  lambdaMax={lambdaMax}
                  sorted_criteria={sorted_criteria}
                  ci={ci}
                  cr={cr}
                  inconsistency_ratios={inconsistency_ratios}
                  extra={extra}
                  matrix={matrix}
                />
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Ahp;
